/**
 * Сценарии тренировок — рабочее место управляющего (§10.12 ТЗ).
 *
 * Шестнадцать ситуаций из письма заказчика заводятся сидингом
 * черновиками, и сотрудник их не видит, пока управляющий не опубликует.
 * Текст роли написан нами, а ресторан знает своих гостей лучше: тренажёр,
 * который играет не того гостя, учит не тому.
 *
 * **Здесь нет ни одного разговора сотрудника** (D64, пункт приёмки 53).
 * Экран показывает роль гостя — то, чему учат, — и не показывает, как
 * по ней отвечали: такого адреса на сервере нет вовсе.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, ErrorState, Field, Loading } from '@shell/index';
import { studyApi } from '../api';
import type { AdminScenario } from '../api';

const KEY = ['admin-scenarios'];

function ScenarioCard({ scenario, onDone }: { scenario: AdminScenario; onDone: () => void }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(scenario.name);
  const [brief, setBrief] = useState(scenario.guest_brief);
  const [note, setNote] = useState(scenario.success_note ?? '');
  const [problem, setProblem] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      studyApi.updateScenario(scenario.id, {
        name: name.trim(),
        guest_brief: brief.trim(),
        success_note: note.trim() || null,
      }),
    onSuccess: () => {
      setEditing(false);
      setProblem(null);
      onDone();
    },
    onError: (error: unknown) =>
      setProblem(error instanceof Error ? error.message : 'Не получилось сохранить.'),
  });
  const toggle = useMutation({
    mutationFn: () =>
      scenario.status === 'published'
        ? studyApi.unpublishScenario(scenario.id)
        : studyApi.publishScenario(scenario.id),
    onSuccess: onDone,
  });

  const published = scenario.status === 'published';

  return (
    <Card title={scenario.name}>
      <p className="faint">
        {published ? 'опубликован — сотрудники его видят' : 'черновик — сотрудники его не видят'}
      </p>

      {editing ? (
        <>
          <Field label="Название">
            <input className="search" value={name} onChange={(event) => setName(event.target.value)} />
          </Field>
          <Field label="Кто гость и как себя ведёт">
            <textarea
              className="search lesson-input"
              rows={6}
              value={brief}
              onChange={(event) => setBrief(event.target.value)}
            />
          </Field>
          <Field label="Что считается хорошей работой">
            <textarea
              className="search"
              rows={3}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </Field>
          <p className="faint">
            Не пишите в роли названия блюд и цены: гость спрашивает, а отвечает сотрудник.
          </p>
          {problem && <p className="demo-notice">{problem}</p>}
          <div className="chips">
            <Button
              small
              variant="primary"
              disabled={!name.trim() || !brief.trim() || save.isPending}
              onClick={() => save.mutate()}
            >
              Сохранить
            </Button>
            <Button small variant="quiet" onClick={() => setEditing(false)}>
              Отмена
            </Button>
          </div>
        </>
      ) : (
        <>
          <p>{scenario.guest_brief}</p>
          {scenario.success_note && (
            <p className="faint">Хорошая работа: {scenario.success_note}</p>
          )}
          <div className="chips">
            <Button
              small
              variant={published ? 'quiet' : 'primary'}
              disabled={toggle.isPending}
              onClick={() => toggle.mutate()}
            >
              {published ? 'Снять с публикации' : 'Опубликовать'}
            </Button>
            <Button small variant="quiet" onClick={() => setEditing(true)}>
              Править
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}

export function EditorScenarios() {
  const queryClient = useQueryClient();
  const scenarios = useQuery({ queryKey: KEY, queryFn: studyApi.adminScenarios });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: KEY });
    void queryClient.invalidateQueries({ queryKey: ['study', 'mentor', 'roleplay'] });
  };

  if (scenarios.isPending) return <Loading />;
  if (scenarios.error || !scenarios.data)
    return <ErrorState error={scenarios.error} onRetry={() => scenarios.refetch()} />;

  const rows = scenarios.data.scenarios;
  const published = rows.filter((row) => row.status === 'published').length;

  return (
    <>
      <p className="faint">
        Ситуаций для тренировки: {rows.length}, опубликовано {published}. Сотрудник видит только
        опубликованные. Разговоры сотрудников здесь не показываются — их видит только сам
        сотрудник.
      </p>
      {rows.map((scenario) => (
        <ScenarioCard key={scenario.id} scenario={scenario} onDone={refresh} />
      ))}
    </>
  );
}
